import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import {
  LEARNING_INSIGHT_APPLICATION_MODE,
  LEARNING_INSIGHT_PRIORITY_LABEL,
  STATUS,
} from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type LearningEvidenceItem = {
  label: string;
  detail: string;
  occurred_at: string;
};

export type LearningInsightApplicationMode =
  (typeof LEARNING_INSIGHT_APPLICATION_MODE)[keyof typeof LEARNING_INSIGHT_APPLICATION_MODE];

export type LearningInsightPriorityLabel =
  (typeof LEARNING_INSIGHT_PRIORITY_LABEL)[keyof typeof LEARNING_INSIGHT_PRIORITY_LABEL];

export type LearningInsight = {
  id: number;
  category: string;
  title: string;
  priority_label: string | null;
  summary: string;
  detailed_context: string;
  interaction_count: number;
  evidence: LearningEvidenceItem[];
  source: string;
  computed_at: string;
  created_at: string;
  updated_at: string;
  confidence_score: number;
  confidence_label: LearningInsightPriorityLabel;
  application_mode: LearningInsightApplicationMode;
  manual_priority_label: LearningInsightPriorityLabel | null;
  application_mode_updated_at: string | null;
  application_mode_expires_at: string | null;
};

export type UpdateLearningInsightApplicationModePayload = {
  insightId: number;
  applicationMode: LearningInsightApplicationMode;
  manualPriorityLabel?: LearningInsightPriorityLabel | null;
};

export type LearningHistoryState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  insights: LearningInsight[];
};

export const learningHistoryInitialState: LearningHistoryState = {
  status: STATUS.IDLE,
  error: null,
  insights: [],
};

function isLearningEvidenceItem(value: unknown): value is LearningEvidenceItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.label === "string" &&
    typeof row.detail === "string" &&
    typeof row.occurred_at === "string"
  );
}

function isLearningInsight(value: unknown): value is LearningInsight {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.category === "string" &&
    typeof row.title === "string" &&
    (typeof row.priority_label === "string" || row.priority_label === null) &&
    typeof row.summary === "string" &&
    typeof row.detailed_context === "string" &&
    typeof row.interaction_count === "number" &&
    Array.isArray(row.evidence) &&
    row.evidence.every(isLearningEvidenceItem) &&
    typeof row.source === "string" &&
    typeof row.computed_at === "string" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string" &&
    typeof row.confidence_score === "number" &&
    typeof row.confidence_label === "string" &&
    typeof row.application_mode === "string" &&
    (typeof row.manual_priority_label === "string" || row.manual_priority_label === null) &&
    (typeof row.application_mode_updated_at === "string" ||
      row.application_mode_updated_at === null) &&
    (typeof row.application_mode_expires_at === "string" ||
      row.application_mode_expires_at === null)
  );
}

function parseLearningHistoryResponse(data: unknown): LearningInsight[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isLearningInsight)) {
    return null;
  }
  return data;
}

export const fetchLearningHistory = createAsyncThunk<
  LearningInsight[],
  void,
  { rejectValue: string }
>("learningHistory/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/context-hub/learning-history", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const insights = parseLearningHistoryResponse(data);
    if (!insights) {
      return rejectWithValue("Unexpected response shape");
    }
    return insights;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** Admin-triggered recompute for one user; returns the user id acted on. */
export const triggerLearningInsightsGeneration = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("learningHistory/triggerGeneration", async (userId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/users/${userId}/learning-insights/generate`, {
      method: "POST",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return userId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** User-triggered Override Learning choice on one of their own insights. */
export const updateLearningInsightApplicationMode = createAsyncThunk<
  LearningInsight,
  UpdateLearningInsightApplicationModePayload,
  { rejectValue: string }
>(
  "learningHistory/updateApplicationMode",
  async ({ insightId, applicationMode, manualPriorityLabel }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/context-hub/learning-history/${insightId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          application_mode: applicationMode,
          manual_priority_label: manualPriorityLabel ?? null,
        }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isLearningInsight(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

const learningHistorySlice = createSlice({
  name: "learningHistory",
  initialState: learningHistoryInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLearningHistory.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchLearningHistory.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.insights = action.payload;
      })
      .addCase(fetchLearningHistory.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(updateLearningInsightApplicationMode.fulfilled, (state, action) => {
        const index = state.insights.findIndex((insight) => insight.id === action.payload.id);
        if (index !== -1) {
          state.insights[index] = action.payload;
        }
      });
  },
});

export const learningHistoryReducer = learningHistorySlice.reducer;

export const selectLearningHistory = (state: RootState) => state.learningHistory.insights;
export const selectLearningHistoryStatus = (state: RootState) => state.learningHistory.status;
